import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/Toast'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { METHOD_LABELS } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import { recordTransfer } from '@/data/income'
import type { FundBalance } from '@/data/income'
import { formatKwacha, parseKwacha } from '@/lib/money'
import { lusakaToday } from '@/lib/periods'

const newId = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`)
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/** Record money sent on from a passed-on fund (for example tithe paid to the Conference). */
export function TransferForm({
  funds,
  initialFundId,
  isOpen,
  onClose,
}: {
  /** Passed-on funds only. */
  funds: FundBalance[]
  initialFundId?: string
  isOpen: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const today = lusakaToday()
  const [fundId, setFundId] = useState(initialFundId ?? funds[0]?.fundId ?? '')
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer')
  const [sentOn, setSentOn] = useState(today)
  const [recipient, setRecipient] = useState('Conference')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const busy = useRef(false)
  const transferId = useRef(newId())
  const toast = useToast()
  const save = useMutation({ mutationFn: (input: Parameters<typeof recordTransfer>[0]) => recordTransfer(input, transferId.current) })

  const wasOpen = useRef(false)
  if (isOpen && !wasOpen.current) {
    wasOpen.current = true
    transferId.current = newId()
    if (initialFundId && initialFundId !== fundId) setFundId(initialFundId)
  }
  if (!isOpen && wasOpen.current) wasOpen.current = false

  const fund = funds.find((f) => f.fundId === (fundId || funds[0]?.fundId))
  const ngwee = parseKwacha(amount)
  const overdrawn = fund !== undefined && ngwee !== null && ngwee > fund.heldNgwee

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current) return
    setError('')
    const chosen = fund?.fundId
    if (!chosen) return setError('Choose which fund this was sent from.')
    if (ngwee === null || ngwee <= 0) return setError('Enter an amount greater than zero.')
    if (!sentOn) return setError('Choose the date it was sent.')
    if (sentOn > today) return setError('The date cannot be in the future.')
    busy.current = true
    try {
      await save.mutateAsync({ fundId: chosen, amountNgwee: ngwee, method, sentOn, recipient, reference, note })
      await qc.invalidateQueries({ queryKey: ['income'] })
      toast.success('Money sent on is recorded. Add the deposit slip photo next.')
      setAmount(''); setReference(''); setNote('')
      onClose()
    } catch (err) {
      setError(errText(err))
    } finally {
      busy.current = false
    }
  }

  return (
    <Sheet isOpen={isOpen} onClose={onClose} title="Record money sent on">
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
        <Select
          label="Sent from"
          value={fund?.fundId ?? ''}
          onChange={(e) => setFundId(e.target.value)}
          options={funds.map((f) => ({ value: f.fundId, label: `${f.name} (${formatKwacha(f.heldNgwee)} held)` }))}
        />
        <Input label="Amount in Kwacha" inputMode="decimal" placeholder="500.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
        {overdrawn && (
          <p role="status" className="text-body text-wait">
            That is more than the {formatKwacha(fund!.heldNgwee)} recorded as held. You can still save it, for example if tithe was sent before this app was used.
          </p>
        )}
        <Select
          label="How it was sent"
          value={method}
          onChange={(e) => setMethod(e.target.value as PaymentMethod)}
          options={(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((m) => ({ value: m, label: METHOD_LABELS[m] }))}
        />
        <Input label="Date sent" type="date" max={today} value={sentOn} onChange={(e) => setSentOn(e.target.value)} />
        <Input label="Sent to" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
        <Input label="Reference or slip number (optional)" value={reference} onChange={(e) => setReference(e.target.value)} />
        <Textarea label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        {error && <p role="alert" className="text-body text-bad">{error}</p>}
        <Button type="submit" size="lg" isLoading={save.isPending}>{save.isPending ? 'Saving...' : 'Save'}</Button>
        <p className="text-body text-muted">After saving, add a photo of the deposit slip or payment confirmation.</p>
      </form>
    </Sheet>
  )
}
